import React from "react";
import _ from "lodash";
import Dialog from "components/dialog";
import Button from "components/form/button";
import { format } from "lib/globalize-util";
import { $, merge } from "glamor";
import Image from "components/items/item/image";
import type { IScope } from "reducers/scope";
import moment from "moment";
import { FormatMessage } from "react-globalize";
import { IOrder } from "records/order";
import { DateCheckbox } from "./date-checkbox";
import * as PropTypes from "prop-types";
import { DATATEST } from "constants/integration";
import { buildOrderDictionary as boDictionary } from "../build-order-utils";
import { BuildOrderActions } from "constants/analytics";
import { themeOptions } from "styles/theme";

export enum ActionType {
    Add = "add",
    Remove = "remove",
}

export interface IShipDateDictionary {
    [key: string]: string[];
}

export interface IShipDateActions {
    [key: string]: ActionType;
}

interface IProduct {
    imagesT: string[];
    available_from?: string;
    available_until?: string;
}

interface IDetails {
    product: string;
    _availability: {
        earliest_available_date: number;
        latest_available_date?: number;
    };
}
export interface IDateOrganizer {
    product: {
        product: IProduct;
        details: IDetails;
    };
    viewDef: {
        header: string;
        fields: Array<{ value: string; label: string; key: string }>;
        image: string;
        sizeGrid: boolean;
        smallImage: boolean;
    };
    shipDatesCounter: string[];
    shipDateDictionary: IShipDateDictionary;
    index: number;
}

interface IDateOrganizerProps {
    dateOrganizer: IDateOrganizer;
    onClose: () => void;
    scope: IScope;
    order: IOrder;
}

export const DateOrganizer: React.FunctionComponent<IDateOrganizerProps> = (
    { dateOrganizer, onClose, scope, order },
    { app },
) => {
    const styles = React.useMemo(() => {
        return {
            base: merge(
                $(" .productDetails", {
                    display: "flex",
                }),
                $(" .headerDetails", {
                    display: "flex",
                    fontSize: "12px",
                    paddingBottom: "10px",
                }),
                $(" .deliveryDetails", {
                    flexGrow: 1,
                    textAlign: "right",
                    flexDirection: "column",
                    display: "flex",
                }),
                $(" .daysLength", {
                    fontWeight: "bold",
                }),
                $(" .fieldsSection", {
                    paddingTop: "10px",
                    borderTop: `1px solid ${themeOptions.palette.lightGrey}`,
                }),
                $(" .deliveriesPill", {
                    backgroundColor: themeOptions.palette.accent1Color,
                    color: themeOptions.palette.canvasColor,
                    padding: "2px 10px",
                    borderRadius: "15px",
                    lineHeight: "30px",
                }),
                $(" .productDetails .header", {
                    fontWeight: "bold",
                    textDecoration: "underline",
                }),
                $(" .availabilityDates .fieldValue", {
                    fontWeight: "bold",
                    paddingLeft: "5px",
                }),
                $(" .imageHolder", {
                    flexGrow: 0,
                    flexShrink: 0,
                    height: "100%",
                    overflow: "hidden",
                    position: "relative",
                    width: "82px",
                    marginRight: "10px",
                    border: `1px solid ${themeOptions.palette.lightGrey}`,
                }),
                $(" .imageHolder > picture", {
                    height: "100%",
                }),
                $(" picture > img", {
                    maxHeight: "100%",
                    objectFit: "contain",
                }),
                $(" .numDeliveries", {
                    fontWeight: "bold",
                    height: "100%",
                }),
                $(" .otherDates", {
                    fontSize: "12px",
                    paddingBottom: "10px",
                }),
                $(" .productCount", {
                    fontSize: "11px",
                    color: themeOptions.palette.grey,
                    paddingLeft: "5px",
                }),
                $(" .addShipDateButton", {
                    textTransform: "none",
                    marginTop: "30px",
                    fontSize: "14px",
                    color: themeOptions.palette.darkGrey,
                }),
                $(" .nullDates", {
                    display: "flex",
                    width: "68%",
                    height: "88px",
                    justifyContent: "center",
                    alignItems: "center",
                    background: themeOptions.palette.lightestGrey,
                    marginBottom: "10px",
                }),
            ),
            actions: merge(
                $(" .save", {
                    marginLeft: "10px !important",
                }),
                $(" .options", {
                    float: "left !important",
                }),
            ),
            contentStyle: {
                width: "min-content",
            },
            select: {
                width: "256px",
            },
        };
    }, []);

    const [shipDateDictionary, setShipDateDictionary] = React.useState<IShipDateDictionary>({});
    const [shipDateAction, setShipDateAction] = React.useState<IShipDateActions>({});

    React.useEffect(() => {
        (global as any).analytics.track(BuildOrderActions.WODateOrganizerProductEntrySelected);
        setShipDateDictionary(dateOrganizer.shipDateDictionary);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const item = React.useMemo(() => {
        return {
            getImageUrl: () => dateOrganizer.product.product.imagesT[0],
        };
    }, [dateOrganizer.product.product.imagesT]);

    const buildOrderDictionary = React.useMemo(
        () => boDictionary(order.__build_order?.ship_dates),
        [order.__build_order?.ship_dates],
    );

    const handleUpdateCart = React.useCallback(() => {
        (global as any).analytics.track(BuildOrderActions.WODateOrganizerProductBuyer);
        app.legacy.sendMessage({
            action: "date-organizer-actions",
            index: dateOrganizer.index,
            productId: dateOrganizer.product.details.product,
            shipDateAction,
        });
        onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shipDateAction]);

    const handleNonBuyerSelectedDeliveries = React.useCallback((shipDateDictionary) => {
        setShipDateDictionary(shipDateDictionary);
    }, []);

    const handleSetShipDateDictionary = React.useCallback((shipDateDictionary) => {
        setShipDateDictionary(shipDateDictionary);
    }, []);

    const nonBuyerSelectedDeliveries = React.useMemo(() => {
        const _nonBuyerSelectedDeliveries = [];
        Object.keys(dateOrganizer.shipDateDictionary).forEach((shipDates, index) => {
            if (!buildOrderDictionary[shipDates]) {
                const [shipStart, shipEnd] = shipDates.split("-");
                _nonBuyerSelectedDeliveries.push(
                    <DateCheckbox
                        key={index}
                        shipStart={shipStart}
                        shipEnd={shipEnd}
                        productCount={dateOrganizer.shipDateDictionary[shipDates]?.length}
                        shipDateDictionary={shipDateDictionary}
                        productId={dateOrganizer.product.details.product}
                        shipDateAction={shipDateAction}
                        onSetShipDateDictionary={handleNonBuyerSelectedDeliveries}
                        onSetShipDateAction={setShipDateAction}
                        earliestAvailableDate={dateOrganizer.product.details._availability?.earliest_available_date}
                        latestAvailableDate={dateOrganizer.product.details._availability?.latest_available_date}
                    />,
                );
            }
        });
        return _nonBuyerSelectedDeliveries;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dateOrganizer,
        buildOrderDictionary,
        setShipDateDictionary,
        shipDateAction,
        setShipDateAction,
        shipDateDictionary,
    ]);

    const viewDefFields = React.useMemo(
        () =>
            _.map(_.take(dateOrganizer?.viewDef?.fields, 2), ({ value, label, key }) => (
                <div key={key}>
                    {label && <span className="fieldLabel">{`${label}: `}</span>}
                    <span className="fieldValue">{value}</span>
                </div>
            )),
        [dateOrganizer?.viewDef?.fields],
    );

    return (
        <React.Fragment>
            <Dialog
                title={format("Select product shipment(s)")}
                modal={false}
                repositionOnUpdate={false}
                autoScrollBodyContent={false}
                open={!!dateOrganizer}
                onRequestClose={onClose}
                contentClassName={`${styles.base}`}
                actionsContainerClassName={`${styles.actions}`}
                actions={[
                    <Button label={format("Cancel")} key="cancel" primary={false} onClick={onClose} />,
                    <Button
                        className="save"
                        label={format("Apply")}
                        key="save"
                        primary={true}
                        onClick={handleUpdateCart}
                    />,
                ]}
            >
                <div className="headerDetails">
                    <div className="productDetails">
                        {dateOrganizer.viewDef.smallImage && (
                            <div className="imageHolder">
                                <Image item={item} scope={scope} />
                            </div>
                        )}
                        <div className="detailsFields">
                            <div className="header" data-test={DATATEST.ORDERS.DATE_ORGANIZER.PRODUCT_TITLE}>
                                {dateOrganizer.viewDef.header}
                            </div>
                            {viewDefFields}
                            <div className="availabilityDates">
                                <span className="fieldLabel">{`${format("Avail")}: `}</span>
                                <span className="fieldValue">{`${moment
                                    .utc(dateOrganizer.product.product.available_from)
                                    .format("MM/DD/YYYY")} - ${
                                    dateOrganizer.product.product.available_until
                                        ? moment.utc(dateOrganizer.product.product.available_until).format("MM/DD/YYYY")
                                        : ""
                                }`}</span>
                            </div>
                        </div>
                    </div>
                    <div className="deliveryDetails">
                        <FormatMessage
                            className="numDeliveries"
                            path="Product in [span]|numDeliveries|[span] deliveries"
                            variables={{ numDeliveries: dateOrganizer.shipDatesCounter.length }}
                            elements={{ span: <span className="deliveriesPill" /> }}
                        />
                        <FormatMessage
                            className="windowLength"
                            path="|brand| has [span]|windowLength| day[span] ship window"
                            variables={{
                                brand: scope.brand?.name,
                                windowLength: scope.brand?.getPreference?.("default_delivery_window"),
                            }}
                            elements={{ span: <span className="daysLength" /> }}
                        />
                    </div>
                </div>
                <div className="fieldsSection">
                    {order.__build_order?.ship_dates?.length ? (
                        order.__build_order?.ship_dates?.map((shipDate, index) => {
                            return (
                                <DateCheckbox
                                    key={index}
                                    shipStart={shipDate.ship_start}
                                    shipEnd={shipDate.ship_end}
                                    productCount={shipDate.line_items?.length}
                                    shipDateDictionary={shipDateDictionary}
                                    productId={dateOrganizer.product.details.product}
                                    onSetShipDateDictionary={handleSetShipDateDictionary}
                                    shipDateAction={shipDateAction}
                                    onSetShipDateAction={setShipDateAction}
                                    earliestAvailableDate={
                                        dateOrganizer.product.details._availability?.earliest_available_date
                                    }
                                    latestAvailableDate={
                                        dateOrganizer.product.details._availability?.latest_available_date
                                    }
                                />
                            );
                        })
                    ) : (
                        <div className="nullDates">{format("No ship dates present.")}</div>
                    )}
                </div>
                {nonBuyerSelectedDeliveries.length > 0 && (
                    <div className="fieldsSection">
                        <div className="otherDates">
                            {format(
                                "There are products in your cart that fall outside your selected ship dates. The dates below are based on these products.",
                            )}
                        </div>
                        {nonBuyerSelectedDeliveries}
                    </div>
                )}
            </Dialog>
        </React.Fragment>
    );
};

DateOrganizer.contextTypes = { app: PropTypes.object.isRequired };
